import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Logo from '../components/Logo';

// Espace client — consultation de SON chantier, sans compte ni mot de passe.
// L'accès tient au lien signé reçu par email ; la page ne demande donc rien,
// elle affiche ou elle explique que le lien n'est plus valable.
// Volontairement hors du Layout du site : pas de menu, pas de blog, pas de
// bouton « devis gratuit » — le client est déjà client.

const STATUTS = {
  prepare:  { label: 'En préparation', desc: "Nous organisons le chantier et planifions les équipes.", color: 'bg-amber-100 text-amber-800 border-amber-200' },
  en_cours: { label: 'Travaux en cours', desc: 'Les équipes sont à pied d’œuvre.', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  termine:  { label: 'Chantier terminé', desc: 'Les travaux sont réceptionnés.', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  annule:   { label: 'Chantier annulé', desc: '', color: 'bg-red-100 text-red-800 border-red-200' },
};

const fmtDate = (ds) => ds ? new Date(`${ds}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
const fmtEur = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtTaille = (o) => o > 1048576 ? `${(o / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(o / 1024))} Ko`;

export default function EspaceClient() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    fetch(`/api/espace/${token}`)
      .then(async r => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Lien invalide');
        return r.json();
      })
      .then(setData)
      .catch(e => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [token]);

  if (chargement) {
    return <Ecran><p className="text-slate-400">Chargement de votre espace…</p></Ecran>;
  }

  if (erreur) {
    return (
      <Ecran>
        <h1 className="text-2xl font-bold text-slate-900 mb-3">Ce lien n'est plus valable</h1>
        <p className="text-slate-500 mb-6 leading-relaxed">
          Il a peut-être expiré, ou un nouveau lien vous a été envoyé depuis. Écrivez-nous et nous
          vous en renvoyons un tout de suite.
        </p>
        <a href="mailto:contact@apexrenovation.fr" className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors">
          contact@apexrenovation.fr
        </a>
      </Ecran>
    );
  }

  const statut = STATUTS[data.statut] || STATUTS.prepare;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-4">
          <Logo dark className="h-9 w-auto" />
          <span className="text-xs text-slate-400 font-mono">{data.ref}</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <p className="text-sm text-slate-400">Bonjour {data.client.prenom}</p>
          <h1 className="text-2xl font-bold text-slate-900 mt-1 mb-4">Votre chantier</h1>
          <div className={`inline-flex items-center gap-2 text-sm font-semibold border rounded-full px-3 py-1 ${statut.color}`}>
            {statut.label}
          </div>
          {statut.desc && <p className="text-slate-500 text-sm mt-3">{statut.desc}</p>}

          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-100">
            {data.adresse && <Info label="Adresse" value={data.adresse} />}
            {data.dateDebut && <Info label="Début prévu" value={fmtDate(data.dateDebut)} />}
            {data.montantTTC > 0 && <Info label="Montant TTC" value={fmtEur(data.montantTTC)} />}
          </dl>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-1">Vos documents</h2>
          <p className="text-sm text-slate-400 mb-4">Devis, factures et attestations liés à votre chantier.</p>
          {data.documents.length === 0 ? (
            <p className="text-slate-400 text-sm bg-slate-50 rounded-xl p-6 text-center">
              Aucun document pour l'instant — ils apparaîtront ici au fur et à mesure.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.documents.map(doc => (
                <li key={doc.id} className="flex items-center gap-3 py-3">
                  <span className="text-xl shrink-0">📄</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{doc.nom}</p>
                    <p className="text-xs text-slate-400">{fmtTaille(doc.taille)}</p>
                  </div>
                  <a
                    href={`/api/espace/${token}/document/${doc.id}`}
                    className="shrink-0 text-sm font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:border-orange-400 rounded-lg px-3 py-1.5 transition-colors">
                    Télécharger
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>

        {data.factures?.length > 0 && (
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Vos factures</h2>
            <p className="text-sm text-slate-400 mb-4">Acomptes, situations de travaux et solde.</p>
            <ul className="divide-y divide-slate-100">
              {data.factures.map(f => (
                <li key={f.id} className="flex items-center gap-3 py-3">
                  <span className="text-xl shrink-0">{f.type === 'avoir' ? '↩️' : '🧾'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">
                      {f.numero}
                      <span className="ml-2 font-normal text-slate-400 text-sm">
                        {f.type === 'acompte' ? 'Acompte' : f.type === 'situation' ? 'Situation de travaux' : f.type === 'solde' ? 'Solde' : 'Avoir'}
                      </span>
                    </p>
                    <p className="text-xs text-slate-400">
                      {fmtDate(f.dateEmission?.slice(0, 10))}
                      {/* Un avoir ne se règle pas : il vient en déduction. */}
                      {f.type === 'avoir'
                        ? ' · à déduire de votre solde'
                        : f.payee
                          ? ' · réglée'
                          : f.dateEcheance ? ` · à régler avant le ${fmtDate(f.dateEcheance.slice(0, 10))}` : ''}
                    </p>
                  </div>
                  <span className={`shrink-0 font-bold tabular-nums ${f.payee ? 'text-slate-400' : 'text-slate-800'}`}>
                    {fmtEur(f.montantTTC)}
                  </span>
                  <a href={`/api/espace/${token}/facture/${f.id}`} target="_blank" rel="noopener noreferrer"
                     className="shrink-0 text-sm font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:border-orange-400 rounded-lg px-3 py-1.5 transition-colors">
                    Voir
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-1">Avancement</h2>
          <p className="text-sm text-slate-400 mb-4">Ce que nous avons fait, étape par étape.</p>
          {data.etapes.length === 0 ? (
            <p className="text-slate-400 text-sm bg-slate-50 rounded-xl p-6 text-center">
              Le chantier n'a pas encore commencé.
            </p>
          ) : (
            <ol className="relative border-l-2 border-slate-100 ml-2">
              {data.etapes.map(e => (
                <li key={e.id} className="ml-5 pb-6 last:pb-0">
                  <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-orange-500 border-2 border-white" />
                  <p className="text-xs text-slate-400">{fmtDate(e.date)}</p>
                  <h3 className="font-semibold text-slate-900 mt-0.5">{e.titre}</h3>
                  {e.detail && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{e.detail}</p>}
                  {e.photos?.length > 0 && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {e.photos.map(id => (
                        <img key={id} src={`/api/photo/${id}`} alt="" loading="lazy"
                             className="w-24 h-24 object-cover rounded-lg border border-slate-200" />
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>

        <p className="text-center text-sm text-slate-400 pb-4">
          Une question ? <a href="mailto:contact@apexrenovation.fr" className="text-orange-600 font-medium">Écrivez-nous</a> ou appelez votre conducteur de travaux.
        </p>
      </main>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</dt>
      <dd className="text-slate-800 font-medium mt-0.5">{value}</dd>
    </div>
  );
}

function Ecran({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md w-full text-center">
        <div className="flex justify-center mb-6"><Logo className="h-10 w-auto" /></div>
        {children}
      </div>
    </div>
  );
}
